<script>
import Loading from "@/components/Loading.vue";
import Modal from "@/components/Modal.vue";
import { getCurrentOrg, editOrg, getFundusCameras, registerFundusCamera } from "@/services/orgServices"
import StatesJSON from "@/constants/states"

export default{
  components: {
    Loading,
    Modal,
  },

  created(){
    this.org_id = this.$route.params.id;
    this.loadFundusCameras();
    this.get_data(this.org_id);
  },

  data() {
    return {
      org_id: '',
      raw_org_data: {},
      org_data: {
        organization_name: "",
        organization_type: "clinic",
        hq_address: "",
        city: "",
        state: "",
        country: "Malaysia",
        postal_code: "",
        latitude: "",
        longitude: "",
        contact_email: "",
        contact_phone: "",
        subscription_plan: "",
        subscription_type: "",
        subscription_status: "",
        fundus_camera_uuid: "",
        is_active: false
      },
      fundusCameraOptions: [],
      newFundusCamera: {
        brand: "",
        model: "",
      },
      cameraFormError: null,
      isAddingFundusCamera: false,
      isLoading: false,
      modal: {
        isSubmitting: false,
      },
      isEditing: false,
      states: StatesJSON,
      subscriptionTypes: ['Government', 'Private'],
      validationError: null
    }
  },
  methods: {
    async loadFundusCameras(){
      try {
        const result = await getFundusCameras();
        this.fundusCameraOptions = result.data || [];
      } catch (error) {
        console.log(error);
      }
    },

    async get_data(uuid){
      this.isLoading = true
      const result = await getCurrentOrg(uuid);

      if(result.data){
        this.raw_org_data  = result.data;
        this.convert_data(this.raw_org_data);
      }
      this.isLoading = false;
    },

    convert_data(input_data){
      this.org_data = {
        organization_name: input_data.OrganizationName || "",
        organization_type: input_data.OrganizationType || "clinic",
        hq_address: input_data.HQAddress || "",
        city: input_data.City || "",
        state: input_data.State || "",
        country: "Malaysia",
        postal_code: input_data.PostalCode || "",
        latitude: input_data.Latitude || "",
        longitude: input_data.Longitude || "",
        contact_email: input_data.ContactEmail || "",
        contact_phone: input_data.ContactPhone || "",
        subscription_plan: input_data.SubscriptionPlan || "",
        subscription_type: input_data.SubscriptionType || "",
        subscription_status: input_data.SubscriptionStatus || "",
        fundus_camera_uuid: input_data.FundusCameraUuid || "",
        is_active: input_data.isActive !== undefined ? input_data.isActive : false
      };
      this.validationError = null;
    },

    close_modal(){
      this.modal.isSubmitting = false;
      this.isEditing = false;
      this.convert_data(this.raw_org_data);
    },

    validateForm() {
      if (!this.org_data.organization_name.trim()) {
        this.validationError = "Organization name is required.";
        return false;
      }
      if (!this.org_data.organization_type) {
        this.validationError = "Organization type is required.";
        return false;
      }
      this.validationError = null;
      return true;
    },

    openSubmitModal() {
      if (this.validateForm()) {
        this.modal.isSubmitting = true;
      }
    },

    async submit_edit(){
      this.isLoading = true;
      this.modal.isSubmitting = false;
      this.isEditing = false;
      const result = await editOrg(this.org_data, this.org_id);
      if(result){
        await this.get_data(this.org_id);
      }
      this.isLoading = false;
    },

    toggleActiveStatus() {
      this.org_data.is_active = !this.org_data.is_active;
    },

    allowOnlyDigits(event) {
      this.org_data.contact_phone = event.target.value.replace(/\D/g, '');
    },

    async addFundusCamera() {
      const brand = this.newFundusCamera.brand.trim();
      const model = this.newFundusCamera.model.trim();

      if (!brand || !model) {
        this.cameraFormError = "Brand and model are required.";
        return;
      }

      this.isAddingFundusCamera = true;
      this.cameraFormError = null;

      try {
        const result = await registerFundusCamera({ brand, model });
        if (result.data) {
          await this.loadFundusCameras();
          this.org_data.fundus_camera_uuid = result.data.FundusCameraUuid;
          this.newFundusCamera = { brand: "", model: "" };
        }
      } catch (error) {
        console.log(error);
        this.cameraFormError = error.response?.data?.detail || "Unable to add fundus camera.";
      } finally {
        this.isAddingFundusCamera = false;
      }
    },
  }
}
</script>

<template>
  <transition name="fading">
    <Loading v-if="isLoading"/>
  </transition>

  <Modal
    :isVisible="modal.isSubmitting"
    promptText="Save the organization changes?"
    yesText="Save"
    noText="Cancel"
    @yes="submit_edit()"
    @no="close_modal()"
  />

  <div class="admin-page">
    <div class="page-hero">
      <div>
        <p class="page-kicker">Workspace Registry</p>
        <h1 class="page-title">{{ raw_org_data.OrganizationName || 'Edit organization' }}</h1>
        <p class="page-subtitle">Keep required identity fields separate from optional contact, address, and subscription details.</p>
      </div>
      <button class="ui-button secondary" @click="$router.push('/org')">
        <i class="fas fa-arrow-left"></i> Back to organizations
      </button>
    </div>

    <div class="ui-card">
      <div class="ui-card-body">
        <div v-if="validationError" class="alert-message">
          <i class="fas fa-exclamation-circle"></i> {{ validationError }}
        </div>

        <section>
          <div class="section-heading">
            <div>
              <h2 class="section-title">Organization basics</h2>
              <p class="section-description">The minimum identity needed for a workspace.</p>
            </div>
            <span class="badge-soft" :class="{ success: isEditing }">{{ isEditing ? 'Editing' : 'View mode' }}</span>
          </div>

          <div class="form-grid">
            <div class="form-section">
              <label class="field-label">Organization name <span class="required">*</span></label>
              <input class="ui-input" :disabled="!isEditing" v-model="org_data.organization_name">
            </div>
            <div class="form-section">
              <label class="field-label">Organization type <span class="required">*</span></label>
              <select class="ui-select" :disabled="!isEditing" v-model="org_data.organization_type">
                <option value="clinic">Clinic group</option>
                <option value="hospital">Hospital group</option>
              </select>
            </div>
            <div class="form-section">
              <label class="field-label">Active status</label>
              <button class="status-toggle" :class="{ active: org_data.is_active }" :disabled="!isEditing" @click="toggleActiveStatus">
                {{ org_data.is_active ? 'Active' : 'Inactive' }}
              </button>
            </div>
          </div>
        </section>

        <section class="mt-4">
          <div class="section-heading">
            <div>
              <h2 class="section-title">Fundus camera</h2>
              <p class="section-description">Select the camera model used by this organization.</p>
            </div>
            <span class="optional-pill">Optional</span>
          </div>

          <div class="form-grid">
            <div class="form-section full-width">
              <label class="field-label">Assigned camera</label>
              <select class="ui-select" :disabled="!isEditing" v-model="org_data.fundus_camera_uuid">
                <option value="">Not assigned</option>
                <option
                  v-for="camera in fundusCameraOptions"
                  :key="camera.FundusCameraUuid"
                  :value="camera.FundusCameraUuid"
                >
                  {{ camera.DisplayName || `${camera.Brand} : ${camera.Model}` }}
                </option>
              </select>
            </div>
          </div>

          <div v-if="isEditing" class="camera-add-panel">
            <div v-if="cameraFormError" class="alert-message camera-error">
              <i class="fas fa-exclamation-circle"></i> {{ cameraFormError }}
            </div>
            <div class="camera-add-grid">
              <div class="form-section">
                <label class="field-label">New brand</label>
                <input class="ui-input" v-model="newFundusCamera.brand" placeholder="e.g. TOPCON">
              </div>
              <div class="form-section">
                <label class="field-label">New model</label>
                <input class="ui-input" v-model="newFundusCamera.model" placeholder="e.g. NW500">
              </div>
              <button class="ui-button secondary camera-add-button" :disabled="isAddingFundusCamera" @click="addFundusCamera">
                <i class="fas" :class="isAddingFundusCamera ? 'fa-spinner fa-spin' : 'fa-plus'"></i>
                {{ isAddingFundusCamera ? 'Adding...' : 'Add camera' }}
              </button>
            </div>
          </div>
        </section>

        <section class="mt-4">
          <div class="section-heading">
            <div>
              <h2 class="section-title">Contact & address</h2>
              <p class="section-description">Optional details that make the organization easier to identify later.</p>
            </div>
            <span class="optional-pill">Optional</span>
          </div>

          <div class="form-grid">
            <div class="form-section">
              <label class="field-label">Contact email</label>
              <input class="ui-input" :disabled="!isEditing" v-model="org_data.contact_email">
            </div>
            <div class="form-section">
              <label class="field-label">Contact phone</label>
              <input class="ui-input" :disabled="!isEditing" v-model="org_data.contact_phone" @input="allowOnlyDigits">
            </div>
            <div class="form-section full-width">
              <label class="field-label">HQ address</label>
              <textarea class="ui-textarea" :disabled="!isEditing" v-model="org_data.hq_address"></textarea>
            </div>
            <div class="form-section">
              <label class="field-label">City</label>
              <input class="ui-input" :disabled="!isEditing" v-model="org_data.city">
            </div>
            <div class="form-section">
              <label class="field-label">State</label>
              <select class="ui-select" :disabled="!isEditing" v-model="org_data.state">
                <option value="">Choose state</option>
                <option v-for="state in states" :key="state" :value="state">{{ state }}</option>
              </select>
            </div>
            <div class="form-section">
              <label class="field-label">Postal code</label>
              <input class="ui-input" :disabled="!isEditing" v-model="org_data.postal_code">
            </div>
          </div>
        </section>

        <section class="mt-4">
          <div class="section-heading">
            <div>
              <h2 class="section-title">Subscription</h2>
              <p class="section-description">Commercial metadata. Leave blank if not decided yet.</p>
            </div>
            <span class="optional-pill">Optional</span>
          </div>

          <div class="form-grid three">
            <div class="form-section">
              <label class="field-label">Subscription plan</label>
              <input class="ui-input" :disabled="!isEditing" v-model="org_data.subscription_plan">
            </div>
            <div class="form-section">
              <label class="field-label">Subscription type</label>
              <select class="ui-select" :disabled="!isEditing" v-model="org_data.subscription_type">
                <option value="">Not decided</option>
                <option v-for="type in subscriptionTypes" :key="type" :value="type">{{ type }}</option>
              </select>
            </div>
            <div class="form-section">
              <label class="field-label">Subscription status</label>
              <input class="ui-input" :disabled="!isEditing" v-model="org_data.subscription_status">
            </div>
          </div>
        </section>

        <div class="ui-actions">
          <button class="ui-button secondary" v-if="!isEditing" @click="$router.push('/org')">Back</button>
          <button class="ui-button primary" v-if="!isEditing" @click="isEditing = true">
            <i class="fas fa-edit"></i> Edit organization
          </button>
          <button class="ui-button secondary" v-if="isEditing" @click="close_modal()">Cancel</button>
          <button class="ui-button primary" v-if="isEditing" @click="openSubmitModal()">
            <i class="fas fa-save"></i> Save changes
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.status-toggle {
  min-height: 44px;
  min-width: 120px;
  border: 1px solid var(--ui-border);
  border-radius: 999px;
  background: #f2eef4;
  color: var(--ui-muted);
  font-weight: 800;
}

.status-toggle.active {
  background: #eafaf2;
  color: #167147;
  border-color: rgba(35, 189, 115, 0.24);
}

.status-toggle:disabled {
  cursor: not-allowed;
}

.camera-add-panel {
  margin-top: 14px;
  padding-top: 16px;
  border-top: 1px solid var(--ui-border);
}

.camera-add-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  align-items: end;
  gap: 12px;
}

.camera-add-button {
  min-width: 132px;
}

.camera-error {
  margin-bottom: 12px;
}

@media (max-width: 900px) {
  .camera-add-grid {
    grid-template-columns: 1fr;
  }

  .camera-add-button {
    width: 100%;
  }
}
</style>
